import { CatalogBrandsProductsData, FilterData } from '@/entities/catalog-brands'

import { apiSlice } from '@/shared/api/apiSlice'
import { ServerResponse } from '@/shared/types/common'
import { TServerBrandSection, TServerSection } from '@/shared/types/deprecated-types'

interface GetProductReqProps {
	params: Record<string, string>
	filterUrl: string
	brandCode: string
	categoryCode?: string
	regionCookies?: string
	merge?: boolean
}

interface GetBrandSectionProductsProps {
	brandCode: string
	params?: Record<string, string>
	regionCookies?: string
}

interface GetCatalogBrandsDataProps {
	brandCode: string
	categoryCode: string
	resolvedUrl: string
	regionCookies?: string
}

interface GetCatalogBrandsFilterUrl {
	brandCode: string
	categoryCode: string
	filterUrl: string
}

interface GetCatalogBrandsCategoriesProps {
	brandCode: string
	regionCookies?: string
}

interface GetBrandDetailedSectionProps {
	category: string
	regionCookies?: string
}

export interface GetCatalogBrandsCategoryRequest {
	brand: string
	category?: string
}

export interface GetCatalogBrandsCategoryResponse
	extends Pick<TServerSection, 'ID' | 'NAME' | 'DESCRIPTION' | 'PICTURE' | 'SEO'> {}

// Тип для данных категорий бренда
type BrandCategoryItem = {
	NAME: string
	COUNT: number
}

type BrandCategoriesData = {
	[key: string]: BrandCategoryItem
}

export const catalogBrandsApiSlice = apiSlice.injectEndpoints({
	endpoints: build => ({
		getBrandProducts: build.query<CatalogBrandsProductsData, GetProductReqProps>({
			query: ({ params, filterUrl, brandCode, categoryCode, regionCookies = '' }) => {
				// Если есть категория, но нет фильтра, то формируем фильтр по категории
				let finalFilter = filterUrl
				if (categoryCode && (!filterUrl || filterUrl === '{}')) {
					finalFilter = JSON.stringify({
						LIST_SECTION_CODE: {
							type: '=',
							value: [categoryCode]
						}
					})
				}

				return {
					url: `index/brand/${brandCode}/products`,
					params: {
						filter: finalFilter,
						limit: 24,
						...params
					},
					headers: {
						Cookie: regionCookies
					}
				}
			},
			serializeQueryArgs: ({ endpointName, queryArgs }) => {
				return `${endpointName}-${queryArgs.brandCode}-${JSON.stringify(queryArgs.params)}-${queryArgs.filterUrl}`
			},
			transformResponse: result => result['data']
		}),

		getCatalogBrandsFilter: build.query<FilterData, GetCatalogBrandsDataProps>({
			query: ({ brandCode, categoryCode, resolvedUrl, regionCookies = '' }) => ({
				url: `index/brand/${brandCode}/filter/${categoryCode || ''}`,
				params: {
					['sef-url']: resolvedUrl
				},
				headers: {
					Cookie: regionCookies
				}
			}),
			serializeQueryArgs: ({ endpointName, queryArgs }) => {
				return `${endpointName}-${queryArgs.brandCode}-${queryArgs.categoryCode}-${queryArgs.resolvedUrl}`
			},
			transformResponse: result => result['data']
		}),

		getCatalogBrandsFilterUrl: build.query<string, GetCatalogBrandsFilterUrl>({
			query: ({ brandCode, categoryCode, filterUrl }) => ({
				url: `index/brand/${brandCode}/filter/${categoryCode || ''}/sef-url`,
				params: {
					filter: filterUrl
				}
			}),
			transformResponse: result => result['data']
		}),

		getBrandCategories: build.query<any, GetCatalogBrandsCategoriesProps>({
			query: ({ brandCode, regionCookies = '' }) => ({
				url: `index/brand/${brandCode}/categories`,
				headers: {
					Cookie: regionCookies
				}
			}),
			transformResponse: result => result['data']
		}),
		getCatalogBrandsCategory: build.query<GetCatalogBrandsCategoryResponse, GetCatalogBrandsCategoryRequest>({
			query: ({ brand, category }) => (category ? `index/brand/${brand}/${category}` : `index/brand/${brand}`),
			transformResponse: (result: ServerResponse<GetCatalogBrandsCategoryResponse>) => result?.['data'] || null
		}),
		getCatalogBrandsCategories: build.query<BrandCategoriesData, GetCatalogBrandsCategoriesProps>({
			query: ({ brandCode, regionCookies = '' }) => ({
				url: `index/brand/${brandCode}/categories`,
				headers: {
					Cookie: regionCookies
				}
			}),
			transformResponse: result => result['data']
		}),
		getBrandDetailedSection: build.query<TServerBrandSection, GetBrandDetailedSectionProps>({
			query: ({ category, regionCookies = '' }) => ({
				url: `index/brand/${category}/seo`,
				headers: {
					Cookie: regionCookies
				}
			}),
			transformResponse: result => result['data']
		}),
		// Запрос для получения всех продуктов раздела бренда
		getBrandSectionProducts: build.query<CatalogBrandsProductsData, GetBrandSectionProductsProps>({
			query: ({ brandCode, params = {}, regionCookies = '' }) => ({
				url: `index/brand/${brandCode}/products`,
				params: {
					limit: 24,
					...params
				},
				headers: {
					Cookie: regionCookies
				}
			}),
			serializeQueryArgs: ({ endpointName, queryArgs }) => {
				return `${endpointName}-${queryArgs.brandCode}-${JSON.stringify(queryArgs.params)}`
			},
			transformResponse: result => result['data']
		})
	})
})

export const {
	useGetCatalogBrandsFilterQuery,
	useGetCatalogBrandsFilterUrlQuery,
	useLazyGetCatalogBrandsFilterUrlQuery,
	useGetBrandProductsQuery,
	useGetCatalogBrandsCategoriesQuery,
	useLazyGetCatalogBrandsFilterQuery,
	useLazyGetBrandCategoriesQuery,
	useLazyGetCatalogBrandsCategoriesQuery,
	useGetBrandDetailedSectionQuery,
	useLazyGetBrandDetailedSectionQuery,
	useGetBrandSectionProductsQuery,
	useLazyGetBrandSectionProductsQuery
} = catalogBrandsApiSlice
